import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    LinearProgress,
    Alert,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Avatar,
    Divider,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';

// icons
import {
    IconChartLine,
    IconClock,
    IconTrendingUp,
    IconTrendingDown,
    IconRefresh,
    IconDownload,
    IconFileAnalytics,
    IconAlertCircle,
    IconCheck,
    IconArrowUp,
    IconArrowDown,
    IconDots
} from '@tabler/icons-react';

const DatabaseAnalytics = () => {
    const theme = useTheme();
    const [timeRange, setTimeRange] = useState('7d');
    const [selectedDatabase, setSelectedDatabase] = useState('all');
    const [tabValue, setTabValue] = useState(0);
    const [viewMode, setViewMode] = useState('chart');

    // Performance metrics
    const performanceMetrics = {
        queryTime: {
            current: 45,
            previous: 52,
            unit: 'ms',
            trend: 'down'
        },
        throughput: {
            current: 1247,
            previous: 1185,
            unit: 'ops/s',
            trend: 'up'
        },
        connections: {
            current: 87,
            previous: 92,
            unit: 'active',
            trend: 'down'
        },
        cacheHitRate: {
            current: 94.5,
            previous: 91.2,
            unit: '%',
            trend: 'up'
        }
    };

    // Query analytics chart
    const queryChartOptions = {
        chart: {
            type: 'line',
            height: 350,
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        yaxis: {
            title: { text: 'Queries per minute' },
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        colors: [theme.palette.primary.main, theme.palette.secondary.main],
        legend: {
            position: 'top',
            horizontalAlign: 'right'
        },
        grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 5
        }
    };

    const queryChartSeries = [
        {
            name: 'Read Operations',
            data: [120, 145, 130, 165, 155, 140, 135]
        },
        {
            name: 'Write Operations',
            data: [45, 55, 48, 62, 58, 52, 50]
        }
    ];

    // Collection size chart
    const sizeChartOptions = {
        chart: {
            type: 'donut',
            height: 350
        },
        labels: ['users', 'orders', 'products', 'sessions', 'logs'],
        colors: ['#1976d2', '#42a5f5', '#66bb6a', '#ffa726', '#ef5350'],
        dataLabels: {
            enabled: true,
            formatter: (val) => `${Math.round(val)}%`
        },
        legend: {
            position: 'bottom'
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total Size',
                            formatter: () => '2.9 GB'
                        }
                    }
                }
            }
        }
    };

    const sizeChartSeries = [45, 128, 22, 312, 2400];

    // Index performance data
    const indexPerformance = [
        { name: '_id_', collection: 'users', operations: 15420, avgTime: '0.8ms', hitRate: '99.2%', size: '2.1 MB' },
        { name: 'email_1', collection: 'users', operations: 8745, avgTime: '1.2ms', hitRate: '87.5%', size: '1.8 MB' },
        { name: 'user_id_1', collection: 'orders', operations: 12847, avgTime: '0.9ms', hitRate: '94.3%', size: '3.2 MB' },
        { name: 'created_at_1', collection: 'sessions', operations: 5623, avgTime: '2.1ms', hitRate: '72.8%', size: '4.5 MB' }
    ];

    // Slow queries
    const slowQueries = [
        {
            query: 'db.users.find({role: "admin", status: {$in: ["active", "pending"]}}).sort({created_at: -1})',
            time: '245ms',
            collection: 'users',
            scanned: 15420,
            returned: 47
        },
        {
            query: 'db.orders.aggregate([{$match: {status: "completed"}}, {$group: {_id: "$user_id", total: {$sum: "$amount"}}}])',
            time: '187ms',
            collection: 'orders',
            scanned: 48392,
            returned: 8234
        },
        {
            query: 'db.sessions.find({duration: {$gt: 3600}}).limit(1000)',
            time: '156ms',
            collection: 'sessions',
            scanned: 125837,
            returned: 1000
        }
    ];

    const renderMetricCard = (title, metric) => (
        <Card>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                            {title}
                        </Typography>
                        <Typography variant="h3">
                            {metric.current}
                            <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                                {metric.unit}
                            </Typography>
                        </Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                            {metric.trend === 'up' ? (
                                <IconArrowUp size={16} color={theme.palette.success.main} />
                            ) : (
                                <IconArrowDown size={16} color={theme.palette.error.main} />
                            )}
                            <Typography variant="caption" color={metric.trend === 'up' ? 'success.main' : 'error.main'}>
                                {Math.abs(((metric.current - metric.previous) / metric.previous) * 100).toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                vs last period
                            </Typography>
                        </Box>
                    </Box>
                    <Avatar
                        sx={{
                            bgcolor: metric.trend === 'up' ? theme.palette.success.light : theme.palette.error.light,
                            width: 40,
                            height: 40
                        }}
                    >
                        {metric.trend === 'up' ? <IconTrendingUp size={20} /> : <IconTrendingDown size={20} />}
                    </Avatar>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            {/* Header Controls */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" gap={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Database</InputLabel>
                        <Select value={selectedDatabase} label="Database" onChange={(e) => setSelectedDatabase(e.target.value)}>
                            <MenuItem value="all">All Databases</MenuItem>
                            <MenuItem value="production_db">production_db</MenuItem>
                            <MenuItem value="analytics_db">analytics_db</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select value={timeRange} label="Time Range" onChange={(e) => setTimeRange(e.target.value)}>
                            <MenuItem value="1h">Last Hour</MenuItem>
                            <MenuItem value="24h">Last 24 Hours</MenuItem>
                            <MenuItem value="7d">Last 7 Days</MenuItem>
                            <MenuItem value="30d">Last 30 Days</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box display="flex" gap={1}>
                    <Button startIcon={<IconRefresh />} size="small">
                        Refresh
                    </Button>
                    <Button startIcon={<IconDownload />} variant="outlined" size="small">
                        Export Report
                    </Button>
                </Box>
            </Box>

            {/* Performance Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard('Avg Query Time', performanceMetrics.queryTime)}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard('Throughput', performanceMetrics.throughput)}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard('Active Connections', performanceMetrics.connections)}
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    {renderMetricCard('Cache Hit Rate', performanceMetrics.cacheHitRate)}
                </Grid>
            </Grid>

            {/* Main Analytics Content */}
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label="Query Analytics" />
                        <Tab label="Storage Analytics" />
                        <Tab label="Index Performance" />
                        <Tab label="Slow Queries" />
                    </Tabs>
                </Box>

                <CardContent>
                    {/* Query Analytics Tab */}
                    {tabValue === 0 && (
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h5">Query Performance Over Time</Typography>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                    size="small"
                                >
                                    <ToggleButton value="chart">
                                        <IconChartLine size={18} />
                                    </ToggleButton>
                                    <ToggleButton value="table">
                                        <IconFileAnalytics size={18} />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {viewMode === 'chart' ? (
                                <ReactApexChart options={queryChartOptions} series={queryChartSeries} type="line" height={350} />
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Time Period</TableCell>
                                                <TableCell align="right">Read Ops</TableCell>
                                                <TableCell align="right">Write Ops</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                                <TableCell align="right">Avg Response Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                                                <TableRow key={day}>
                                                    <TableCell>{day}</TableCell>
                                                    <TableCell align="right">{queryChartSeries[0].data[index]}</TableCell>
                                                    <TableCell align="right">{queryChartSeries[1].data[index]}</TableCell>
                                                    <TableCell align="right">
                                                        {queryChartSeries[0].data[index] + queryChartSeries[1].data[index]}
                                                    </TableCell>
                                                    <TableCell align="right">{(Math.random() * 50 + 30).toFixed(1)}ms</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Grid container spacing={2} sx={{ mt: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Operation Breakdown
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemText primary="find" secondary="45,293 operations" />
                                                    <Chip label="42%" size="small" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="insertOne" secondary="23,847 operations" />
                                                    <Chip label="22%" size="small" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="updateOne" secondary="18,293 operations" />
                                                    <Chip label="17%" size="small" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="aggregate" secondary="12,837 operations" />
                                                    <Chip label="12%" size="small" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemText primary="deleteOne" secondary="7,523 operations" />
                                                    <Chip label="7%" size="small" />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Peak Usage Times
                                            </Typography>
                                            <List>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconClock />
                                                    </ListItemIcon>
                                                    <ListItemText primary="9:00 AM - 11:00 AM" secondary="Morning peak - 3,847 qps" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconClock />
                                                    </ListItemIcon>
                                                    <ListItemText primary="2:00 PM - 4:00 PM" secondary="Afternoon peak - 4,293 qps" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconClock />
                                                    </ListItemIcon>
                                                    <ListItemText primary="6:00 PM - 8:00 PM" secondary="Evening peak - 2,947 qps" />
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Storage Analytics Tab */}
                    {tabValue === 1 && (
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" gutterBottom>
                                        Collection Size Distribution
                                    </Typography>
                                    <ReactApexChart options={sizeChartOptions} series={sizeChartSeries} type="donut" height={350} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h5" gutterBottom>
                                        Storage Metrics
                                    </Typography>
                                    <List>
                                        <ListItem>
                                            <ListItemText
                                                primary="Total Database Size"
                                                secondary={<LinearProgress variant="determinate" value={72} sx={{ mt: 1 }} />}
                                            />
                                            <Typography variant="h6">2.9 GB / 4 GB</Typography>
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Index Size" />
                                            <Typography variant="body1">245 MB</Typography>
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Average Object Size" />
                                            <Typography variant="body1">2.8 KB</Typography>
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Storage Engine" />
                                            <Chip label="WiredTiger" size="small" />
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Compression Ratio" />
                                            <Typography variant="body1" color="success.main">
                                                3.2:1
                                            </Typography>
                                        </ListItem>
                                    </List>

                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Storage usage is at 72% capacity. Consider archiving old data or expanding storage.
                                    </Alert>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Index Performance Tab */}
                    {tabValue === 2 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Index Usage and Performance
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Index Name</TableCell>
                                            <TableCell>Collection</TableCell>
                                            <TableCell align="right">Operations</TableCell>
                                            <TableCell align="right">Avg Time</TableCell>
                                            <TableCell align="right">Hit Rate</TableCell>
                                            <TableCell align="right">Size</TableCell>
                                            <TableCell align="center">Health</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {indexPerformance.map((index) => (
                                            <TableRow key={index.name}>
                                                <TableCell>{index.name}</TableCell>
                                                <TableCell>
                                                    <Chip label={index.collection} size="small" />
                                                </TableCell>
                                                <TableCell align="right">{index.operations.toLocaleString()}</TableCell>
                                                <TableCell align="right">{index.avgTime}</TableCell>
                                                <TableCell align="right">
                                                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={parseFloat(index.hitRate)}
                                                            sx={{ width: 60, mr: 1 }}
                                                            color={parseFloat(index.hitRate) > 90 ? 'success' : 'warning'}
                                                        />
                                                        {index.hitRate}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">{index.size}</TableCell>
                                                <TableCell align="center">
                                                    {parseFloat(index.hitRate) > 90 ? (
                                                        <IconCheck color={theme.palette.success.main} />
                                                    ) : (
                                                        <IconAlertCircle color={theme.palette.warning.main} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Index 'created_at_1' on sessions collection has a low hit rate. Consider reviewing query patterns.
                            </Alert>
                        </Box>
                    )}

                    {/* Slow Queries Tab */}
                    {tabValue === 3 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Slow Query Analysis
                            </Typography>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Found {slowQueries.length} queries exceeding 100ms threshold
                            </Alert>

                            {slowQueries.map((query, index) => (
                                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        bgcolor: 'grey.100',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        mb: 2
                                                    }}
                                                >
                                                    {query.query}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6} sm={3}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Execution Time
                                                        </Typography>
                                                        <Typography variant="body1" color="error">
                                                            {query.time}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Collection
                                                        </Typography>
                                                        <Typography variant="body1">{query.collection}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Documents Scanned
                                                        </Typography>
                                                        <Typography variant="body1">{query.scanned.toLocaleString()}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Documents Returned
                                                        </Typography>
                                                        <Typography variant="body1">{query.returned.toLocaleString()}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <IconButton size="small">
                                                <IconDots />
                                            </IconButton>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="body2" color="primary">
                                            Recommendation: Add index on 'role' and 'status' fields to improve performance
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DatabaseAnalytics;
